import * as React from 'react';

/**
 * Makes an element inert — unfocusable, unclickable, hidden from assistive
 * technology — while `active` is true, for a region another layer covers.
 *
 * An attribute toggle rather than a JSX prop: the React 18 runtime drops
 * `inert={true}` as an unknown boolean, while the `@types/react` in use
 * already types it as one, so the JSX form compiles and does nothing.
 *
 * @param ref The element to cover. A null ref is a no-op for that render.
 * @param active Whether the element is covered right now; flipping it back
 *   removes both attributes.
 * @returns Nothing — the effect writes `inert` and `aria-hidden` onto the
 *   element itself.
 */
export function useInert(ref: React.RefObject<HTMLElement | null>, active: boolean): void {
  React.useLayoutEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    element.toggleAttribute('inert', active);
    if (active) {
      element.setAttribute('aria-hidden', 'true');
    } else {
      element.removeAttribute('aria-hidden');
    }
  }, [ref, active]);
}
